import {
  Box,
  Typography,
  Grid,
  Divider,
  useTheme,
  useMediaQuery,
  Stack,
} from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import Empty from '../../public/assets/empty.png';
import { GenCurrency, GenDateRangeDataFormat } from './GenDataFormat';
import SwipeableEdgeDrawer from './SwipeableEdgeDrawer';

export const GenSummaryCategory = ({ category }) => {
  const theme = useTheme();
  return (
    <Box sx={{ pb: 2 }}>
      <Typography
        variant="overline"
        component="span"
        sx={{ color: theme.palette.text.secondary, lineHeight: 0 }}
      >
        {category.name}:
      </Typography>

      {category.data.map((item, index) => (
        <Grid
          key={index}
          container
          rowGap={1}
          justifyContent="space-between"
          alignItems="center"
        >
          <Grid item>
            <Typography variant="body1" component="span">
              {item.name}
            </Typography>
          </Grid>
          <Grid item>
            <Typography variant="body1" component="span">
              {item.value}
            </Typography>
          </Grid>
        </Grid>
      ))}
    </Box>
  );
};

export const GenSummaryCurrencyDetail = ({ text, value, color }) => {
  return (
    <Grid
      container
      direction="row"
      justifyContent="space-between"
      alignItems="center"
    >
      <Grid item>
        <Typography variant="body1">{text}</Typography>
      </Grid>
      <Grid item>
        <Typography variant="body1" color={color}>
          <GenCurrency value={value} />
        </Typography>
      </Grid>
    </Grid>
  );
};

export const GenSummaryDateRangeDetail = ({ text, dateFrom, dateTo }) => {
  return (
    <Grid
      container
      direction="row"
      justifyContent="space-between"
      alignItems="center"
    >
      <Grid item>
        <Typography variant="body1">{text}</Typography>
      </Grid>
      <Grid item>
        <Typography variant="subtitle1" component="span">
          <GenDateRangeDataFormat dateFrom={dateFrom} dateTo={dateTo} />
        </Typography>
      </Grid>
    </Grid>
  );
};

const GenSummary = ({ data = [], subtitle, footer, children }) => {
  const { t } = useTranslation('global');
  const theme = useTheme();
  const isMediumSizeBreakPoint = useMediaQuery(theme.breakpoints.down('md'));

  const getTitle = () => (
    <Box
      sx={{
        flex: '1 1 auto',
        maxHeight: '80px',
        borderBottom: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(3),
      }}
    >
      <Typography variant="h6">{t('summary.title')}</Typography>
    </Box>
  );
  const getChildren = () =>
    children && (
      <>
        <Divider light />
        <Box
          sx={{
            flex: '1 1 auto',
            maxHeight: '68px',
            display: 'flex',
            padding: theme.spacing(2, 3),
          }}
        >
          {children}
        </Box>
      </>
    );

  const Content = () => (
    <>
      {subtitle && (
        <>
          <Box
            sx={{
              flex: '1 1 auto',
              display: 'flex',
              maxHeight: '68px',
              padding: { xs: theme.spacing(1, 1), md: theme.spacing(2, 3) },
            }}
          >
            {subtitle}
          </Box>
          <Divider light sx={{ display: { xs: 'none', md: 'block' } }} />
        </>
      )}

      {!subtitle && !footer && !data?.length && (
        <Stack
          direction="column"
          alignItems="center"
          justifyContent="center"
          sx={{ height: '100%' }}
        >
          <Box
            sx={{
              flex: '1 1 auto',
              alignItems: 'center',
              justifyContent: 'center',
              padding: theme.spacing(3),
              overflowY: 'auto',
            }}
            display="flex"
            alignItems="center"
            flexDirection="column"
          >
            <img src={Empty} alt="Empty" width="87" />
            <Typography variant="subtitle1" component="span">
              {t('summary.emptyState')}
            </Typography>
          </Box>
        </Stack>
      )}
      {(!!subtitle || !!footer || !!data?.length) && (
        <Box
          sx={{
            flex: '1 1 auto',
            padding: { xs: theme.spacing(1), md: theme.spacing(3) },
            overflowY: 'auto',
          }}
        >
          {data?.map((item, index) => (
            <GenSummaryCategory key={index} category={item} />
          ))}
        </Box>
      )}

      {footer && (
        <>
          <Box
            sx={{
              flex: '1 1 auto',
              maxHeight: '68px',
              display: 'flex',
              padding: { xs: theme.spacing(1, 1), md: theme.spacing(2, 3) },
            }}
          >
            {footer}
          </Box>
          <Divider light sx={{ display: { xs: 'none', md: 'block' } }} />
        </>
      )}
    </>
  );
  return isMediumSizeBreakPoint ? (
    <SwipeableEdgeDrawer
      content={<Content />}
      children={children}
      title={t('summary.title')}
    />
  ) : (
    <Box
      sx={{
        backgroundColor: '#ffffff',
        height: '100%',
        overflow: 'hidden',
        maxHeight: 'calc(100vh - 194px)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <>
        {getTitle()}
        <Content />
        {getChildren()}
      </>
    </Box>
  );
};

export default GenSummary;
